<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import MobilePhonePreview from './MobilePhonePreview.vue';
import PostSelectorModal from './PostSelectorModal.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
  platform: {
    type: String,
    default: 'instagram', // 'instagram' | 'facebook'
  },
  initialData: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['save', 'cancel']);

const store = useStore();

// Form State
const campaignName = ref(props.initialData?.name || '');
const postType = ref(props.initialData?.post_type || 'specific'); // 'specific' | 'all' | 'next'
const selectedPost = ref(
  props.initialData?.target_media_id
    ? {
        id: props.initialData.target_media_id,
        thumbnail_url: props.initialData.target_media_url,
        media_url: props.initialData.target_media_url,
        caption: props.initialData.target_media_caption,
      }
    : null
);

const matchingType = ref(props.initialData?.matching_type === 'any' ? 'any' : 'contains'); // 'contains' | 'any'
const keywordsInput = ref(
  Array.isArray(props.initialData?.keywords)
    ? props.initialData.keywords.join(', ')
    : 'price, info, deal'
);

const replyToCommentsUnderPost = ref(
  props.initialData?.reply_type === 'public_comment' || props.initialData?.reply_type === 'both'
);
const publicReplyMessage = ref(
  props.initialData?.public_reply_message || 'Hi @{{username}}, check your DMs for the details!'
);

const alsoReplyWhenDMs = ref(false);
const openingDmEnabled = ref(false);
const followRequirementEnabled = ref(false);

const dmMessage = ref(
  props.initialData?.dm_message ||
    'Hey {{first_name}}! Thanks for reaching out. Here is the special link you requested: https://example.com'
);

const showPostSelector = ref(false);
const isSaving = ref(false);
const errorMessage = ref('');

// Connected Account Info
const accountHandle = computed(() => {
  return props.inbox?.name || (props.platform === 'instagram' ? 'yourbrand' : 'Your Page');
});

const accountAvatar = computed(() => {
  return props.inbox?.avatar_url || '';
});

// Keywords parsing
const parsedKeywords = computed(() => {
  if (matchingType.value === 'any') return [];
  return keywordsInput.value
    .split(',')
    .map(k => k.trim())
    .filter(Boolean);
});

const handleSelectPost = post => {
  selectedPost.value = post;
  showPostSelector.value = false;
};

const insertTag = tag => {
  dmMessage.value += ` ${tag}`;
};

const insertLink = () => {
  const link = window.prompt('Enter the URL link to include:', 'https://');
  if (link) {
    dmMessage.value += ` ${link}`;
  }
};

const handleSave = async () => {
  errorMessage.value = '';

  if (postType.value === 'specific' && !selectedPost.value?.id) {
    errorMessage.value = 'Please select a specific post or reel, or choose "any post or reel".';
    return;
  }

  if (matchingType.value !== 'any' && parsedKeywords.value.length === 0) {
    errorMessage.value = 'Please enter at least one keyword trigger or select "any word".';
    return;
  }

  if (!dmMessage.value.trim() && !publicReplyMessage.value.trim()) {
    errorMessage.value = 'Please enter a message to send.';
    return;
  }

  let replyType = 'dm';
  if (replyToCommentsUnderPost.value && dmMessage.value.trim()) {
    replyType = 'both';
  } else if (replyToCommentsUnderPost.value) {
    replyType = 'public_comment';
  }

  const payload = {
    inbox_id: props.inbox.id,
    name: campaignName.value.trim() || `${props.platform === 'instagram' ? 'Instagram' : 'Facebook'} Comment Automation`,
    active: true,
    post_type: postType.value === 'all' || postType.value === 'next' ? 'all' : 'specific',
    target_media_id: postType.value === 'specific' ? selectedPost.value?.id : null,
    target_media_url: postType.value === 'specific' ? (selectedPost.value?.thumbnail_url || selectedPost.value?.media_url) : null,
    target_media_caption: postType.value === 'specific' ? selectedPost.value?.caption : null,
    matching_type: matchingType.value === 'any' ? 'any' : 'contains',
    keywords: parsedKeywords.value,
    reply_type: replyType,
    dm_message: dmMessage.value.trim(),
    public_reply_message: replyToCommentsUnderPost.value ? publicReplyMessage.value.trim() : null,
  };

  isSaving.value = true;
  try {
    if (props.initialData?.id) {
      await store.dispatch('commentAutomations/update', {
        id: props.initialData.id,
        ...payload,
      });
    } else {
      await store.dispatch('commentAutomations/create', payload);
    }
    emit('save');
  } catch (error) {
    errorMessage.value =
      error?.response?.data?.error || 'Failed to save automation. Please try again.';
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-surface-1 rounded-2xl overflow-hidden shadow-lg border border-n-weak">
    <!-- Top Bar -->
    <header class="flex items-center justify-between px-8 py-4 border-b border-n-weak bg-n-surface-1 sticky top-0 z-20">
      <div class="flex items-center gap-3">
        <button
          type="button"
          class="p-1.5 rounded-lg hover:bg-n-surface-2 text-n-slate-11 hover:text-n-slate-12 transition-colors"
          @click="emit('cancel')"
        >
          <span class="i-lucide-arrow-left text-lg" />
        </button>
        <div class="flex flex-col">
          <h1 class="text-base font-semibold text-n-slate-12">
            {{ initialData ? 'Edit campaign' : 'New campaign' }}
          </h1>
          <span class="text-xs text-n-slate-10">
            Automated comment keywords for {{ platform === 'instagram' ? 'Instagram' : 'Facebook' }}
          </span>
        </div>
      </div>

      <div class="flex items-center gap-3">
        <Button
          label="Cancel"
          variant="faded"
          size="sm"
          @click="emit('cancel')"
        />
        <Button
          :label="initialData ? 'Save Changes' : 'Go Live'"
          variant="solid"
          color="blue"
          size="sm"
          :is-loading="isSaving"
          @click="handleSave"
        />
      </div>
    </header>

    <!-- Error Banner -->
    <div
      v-if="errorMessage"
      class="mx-8 mt-4 p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400 text-xs flex items-center gap-2"
    >
      <span class="i-lucide-alert-circle text-base shrink-0" />
      <span>{{ errorMessage }}</span>
    </div>

    <!-- Main 2-Column Content Grid -->
    <div class="flex-1 overflow-y-auto p-8 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
      <!-- LEFT COLUMN: Settings & Form -->
      <div class="lg:col-span-7 flex flex-col gap-6 max-w-xl">
        <!-- Campaign Name -->
        <div class="flex flex-col gap-1.5">
          <label class="text-xs font-semibold text-n-slate-12">
            Campaign name <span class="text-n-slate-9 font-normal">(optional)</span>
          </label>
          <input
            v-model="campaignName"
            type="text"
            placeholder="e.g. YC referral or Summer Sale"
            class="w-full px-3.5 py-2.5 rounded-xl border border-n-weak bg-n-surface-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:border-n-brand transition-colors"
          />
        </div>

        <!-- Section: When someone comments on -->
        <div class="flex flex-col gap-3 pt-2">
          <label class="text-xs font-semibold text-n-slate-12">
            When someone comments on
          </label>

          <!-- Option 1: a specific post or reel -->
          <div
            class="flex flex-col p-4 rounded-xl border transition-all cursor-pointer bg-n-surface-1"
            :class="
              postType === 'specific'
                ? 'border-n-brand ring-1 ring-n-brand shadow-sm'
                : 'border-n-weak hover:border-n-slate-6'
            "
            @click="postType = 'specific'"
          >
            <div class="flex items-center gap-3">
              <div
                class="w-4 h-4 rounded-full border flex items-center justify-center transition-colors"
                :class="
                  postType === 'specific'
                    ? 'border-n-brand bg-n-brand'
                    : 'border-n-slate-8'
                "
              >
                <div v-if="postType === 'specific'" class="w-1.5 h-1.5 rounded-full bg-white" />
              </div>
              <span class="text-sm font-medium text-n-slate-12">a specific post or reel</span>
            </div>

            <!-- Post Selector Inside Card -->
            <div v-if="postType === 'specific'" class="mt-3.5 pt-3.5 border-t border-n-weak">
              <div v-if="selectedPost" class="flex items-center justify-between p-2.5 rounded-lg bg-n-surface-2 border border-n-weak">
                <div class="flex items-center gap-3 min-w-0">
                  <img
                    v-if="selectedPost.thumbnail_url || selectedPost.media_url"
                    :src="selectedPost.thumbnail_url || selectedPost.media_url"
                    alt="Post"
                    class="w-12 h-12 rounded-md object-cover shrink-0 bg-zinc-900"
                  />
                  <div class="min-w-0 flex flex-col">
                    <p class="text-xs text-n-slate-12 font-medium truncate max-w-[240px]">
                      {{ selectedPost.caption || 'Selected Post' }}
                    </p>
                    <span class="text-[10px] text-n-slate-10">ID: {{ selectedPost.id }}</span>
                  </div>
                </div>
                <Button
                  label="Change"
                  size="xs"
                  variant="faded"
                  @click.stop="showPostSelector = true"
                />
              </div>

              <div v-else class="flex flex-col items-center justify-center p-6 border-2 border-dashed border-n-weak rounded-xl text-center bg-n-surface-2/50">
                <span class="i-lucide-image text-2xl text-n-slate-10 mb-2" />
                <p class="text-xs text-n-slate-11 mb-3">
                  Select a post or reel from your connected {{ platform === 'instagram' ? 'Instagram' : 'Facebook' }} account.
                </p>
                <Button
                  label="Choose Post or Reel"
                  size="sm"
                  icon="i-lucide-search"
                  @click.stop="showPostSelector = true"
                />
              </div>
            </div>
          </div>

          <!-- Option 2: any post or reel -->
          <div
            class="flex items-center gap-3 p-3.5 rounded-xl border transition-all cursor-pointer bg-n-surface-1"
            :class="
              postType === 'all'
                ? 'border-n-brand ring-1 ring-n-brand shadow-sm'
                : 'border-n-weak hover:border-n-slate-6'
            "
            @click="postType = 'all'"
          >
            <div
              class="w-4 h-4 rounded-full border flex items-center justify-center transition-colors"
              :class="
                postType === 'all'
                  ? 'border-n-brand bg-n-brand'
                  : 'border-n-slate-8'
              "
            >
              <div v-if="postType === 'all'" class="w-1.5 h-1.5 rounded-full bg-white" />
            </div>
            <span class="text-sm font-medium text-n-slate-12">any post or reel</span>
          </div>

          <!-- Option 3: next post or reel -->
          <div
            class="flex items-center gap-3 p-3.5 rounded-xl border transition-all cursor-pointer bg-n-surface-1"
            :class="
              postType === 'next'
                ? 'border-n-brand ring-1 ring-n-brand shadow-sm'
                : 'border-n-weak hover:border-n-slate-6'
            "
            @click="postType = 'next'"
          >
            <div
              class="w-4 h-4 rounded-full border flex items-center justify-center transition-colors"
              :class="
                postType === 'next'
                  ? 'border-n-brand bg-n-brand'
                  : 'border-n-slate-8'
              "
            >
              <div v-if="postType === 'next'" class="w-1.5 h-1.5 rounded-full bg-white" />
            </div>
            <span class="text-sm font-medium text-n-slate-12">next post or reel</span>
          </div>
        </div>

        <!-- Section: And this comment has -->
        <div class="flex flex-col gap-3 pt-2">
          <label class="text-xs font-semibold text-n-slate-12">
            And this comment has
          </label>

          <!-- Specific keywords -->
          <div
            class="flex flex-col p-4 rounded-xl border transition-all cursor-pointer bg-n-surface-1"
            :class="
              matchingType === 'contains'
                ? 'border-n-brand ring-1 ring-n-brand shadow-sm'
                : 'border-n-weak hover:border-n-slate-6'
            "
            @click="matchingType = 'contains'"
          >
            <div class="flex items-center gap-3">
              <div
                class="w-4 h-4 rounded-full border flex items-center justify-center transition-colors"
                :class="
                  matchingType === 'contains'
                    ? 'border-n-brand bg-n-brand'
                    : 'border-n-slate-8'
                "
              >
                <div v-if="matchingType === 'contains'" class="w-1.5 h-1.5 rounded-full bg-white" />
              </div>
              <span class="text-sm font-medium text-n-slate-12">a specific word or words</span>
            </div>

            <div v-if="matchingType === 'contains'" class="mt-3 flex flex-col gap-1.5">
              <input
                v-model="keywordsInput"
                type="text"
                placeholder="Enter a word or multiple (e.g. price, deal, info)"
                class="w-full px-3.5 py-2.5 rounded-xl border border-n-weak bg-n-surface-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:border-n-brand transition-colors"
                @click.stop
              />
              <span class="text-[11px] text-n-slate-10">Use commas to separate words</span>
            </div>
          </div>

          <!-- Any word -->
          <div
            class="flex items-center gap-3 p-3.5 rounded-xl border transition-all cursor-pointer bg-n-surface-1"
            :class="
              matchingType === 'any'
                ? 'border-n-brand ring-1 ring-n-brand shadow-sm'
                : 'border-n-weak hover:border-n-slate-6'
            "
            @click="matchingType = 'any'"
          >
            <div
              class="w-4 h-4 rounded-full border flex items-center justify-center transition-colors"
              :class="
                matchingType === 'any'
                  ? 'border-n-brand bg-n-brand'
                  : 'border-n-slate-8'
              "
            >
              <div v-if="matchingType === 'any'" class="w-1.5 h-1.5 rounded-full bg-white" />
            </div>
            <span class="text-sm font-medium text-n-slate-12">any word</span>
          </div>

          <!-- Secondary Toggles -->
          <div class="flex items-center justify-between p-3.5 rounded-xl border border-n-weak bg-n-surface-1">
            <span class="text-xs font-medium text-n-slate-12">also reply when someone DMs these words</span>
            <Switch v-model="alsoReplyWhenDMs" size="sm" />
          </div>

          <div class="flex flex-col p-3.5 rounded-xl border border-n-weak bg-n-surface-1 gap-2.5">
            <div class="flex items-center justify-between">
              <span class="text-xs font-medium text-n-slate-12">reply to their comments under the post</span>
              <Switch v-model="replyToCommentsUnderPost" size="sm" />
            </div>
            <div v-if="replyToCommentsUnderPost" class="pt-2 border-t border-n-weak flex flex-col gap-1.5">
              <label class="text-[11px] font-medium text-n-slate-11">Public comment reply</label>
              <input
                v-model="publicReplyMessage"
                type="text"
                placeholder="Hi @{{username}}, we just sent you a DM with the link!"
                class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-surface-2 text-xs text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:border-n-brand"
              />
            </div>
          </div>
        </div>

        <!-- Section: They will get -->
        <div class="flex flex-col gap-2.5 pt-2">
          <label class="text-xs font-semibold text-n-slate-12">
            They will get
          </label>
          <div class="flex items-center justify-between p-3.5 rounded-xl border border-n-weak bg-n-surface-1">
            <span class="text-xs font-medium text-n-slate-12">an opening DM</span>
            <Switch v-model="openingDmEnabled" size="sm" />
          </div>
          <div class="flex items-center justify-between p-3.5 rounded-xl border border-n-weak bg-n-surface-1">
            <span class="text-xs font-medium text-n-slate-12">a follow requirement first</span>
            <Switch v-model="followRequirementEnabled" size="sm" />
          </div>
        </div>

        <!-- Section: And then, they will get -->
        <div class="flex flex-col gap-2.5 pt-2">
          <label class="text-xs font-semibold text-n-slate-12">
            And then, they will get
          </label>
          <div class="flex flex-col p-4 rounded-xl border border-n-weak bg-n-surface-1 gap-3 shadow-sm">
            <span class="text-xs font-semibold text-n-slate-12">a DM with a link</span>
            <textarea
              v-model="dmMessage"
              rows="4"
              placeholder="Write a message..."
              class="w-full p-3 rounded-xl border border-n-weak bg-n-surface-2 text-xs text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:border-n-brand leading-relaxed resize-none"
            />
            <div class="flex items-center justify-between">
              <button
                type="button"
                class="px-3 py-1.5 text-xs font-medium rounded-lg border border-n-weak hover:border-n-slate-8 text-n-slate-12 hover:bg-n-surface-2 transition-colors flex items-center gap-1.5 cursor-pointer"
                @click="insertLink"
              >
                <span class="i-lucide-plus text-xs" />
                <span>Add A Link</span>
              </button>

              <div class="flex items-center gap-1">
                <button
                  type="button"
                  class="px-2 py-1 text-[10px] rounded bg-n-surface-2 text-n-slate-11 hover:text-n-slate-12 border border-n-weak"
                  @click="insertTag('{{first_name}}')"
                >
                  + name
                </button>
                <button
                  type="button"
                  class="px-2 py-1 text-[10px] rounded bg-n-surface-2 text-n-slate-11 hover:text-n-slate-12 border border-n-weak"
                  @click="insertTag('@{{username}}')"
                >
                  + username
                </button>
              </div>
            </div>
            <span class="text-[10px] text-n-slate-10 leading-relaxed">
              {link} inserts the tracked link; {username} personalizes; {{first_name}} inserts first name.
            </span>
          </div>
        </div>
      </div>

      <!-- RIGHT COLUMN: Interactive Live Smartphone Preview -->
      <div class="lg:col-span-5 flex flex-col items-center justify-center sticky top-24 pt-4 lg:pt-0">
        <div class="w-full flex justify-between items-center max-w-[310px] mb-3">
          <span class="text-xs font-semibold text-n-slate-11 uppercase tracking-wider">Preview</span>
          <span class="text-[11px] text-n-brand font-medium">Live Demo</span>
        </div>
        <MobilePhonePreview
          :account-name="accountHandle"
          :account-avatar="accountAvatar"
          :platform="platform"
          :dm-message="dmMessage"
          :public-reply-message="publicReplyMessage"
          :selected-post="selectedPost"
          :post-type="postType"
        />
      </div>
    </div>

    <!-- Post Selector Modal -->
    <PostSelectorModal
      v-if="showPostSelector"
      :inbox-id="inbox.id"
      :platform="platform"
      @select="handleSelectPost"
      @close="showPostSelector = false"
    />
  </div>
</template>
